import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './ModerationActionModal.css';

const DURATION_OPTIONS = [
  { label: '1 hour', value: 1 },
  { label: '6 hours', value: 6 },
  { label: '12 hours', value: 12 },
  { label: '1 day', value: 24 },
  { label: '3 days', value: 72 },
  { label: '7 days', value: 168 },
  { label: '14 days', value: 336 },
  { label: '30 days', value: 720 },
];

const REASON_OPTIONS = [
  { value: 'spam', labelKey: 'reportReasons.spam', defaultLabel: 'Spam' },
  { value: 'inappropriate', labelKey: 'reportReasons.inappropriate', defaultLabel: 'Inappropriate Content' },
  { value: 'harassment', labelKey: 'reportReasons.harassment', defaultLabel: 'Harassment or Bullying' },
  { value: 'fake_info', labelKey: 'reportReasons.fake_info', defaultLabel: 'Fake Information' },
  { value: 'violence', labelKey: 'reportReasons.violence', defaultLabel: 'Violence or Threats' },
  { value: 'copyright', labelKey: 'reportReasons.copyright', defaultLabel: 'Copyright Violation' },
  { value: 'animal_cruelty', labelKey: 'reportReasons.animal_cruelty', defaultLabel: 'Animal Cruelty' },
  { value: 'other', labelKey: 'reportReasons.other', defaultLabel: 'Other' },
];

const ModerationActionModal = ({
  isOpen,
  onClose,
  title,
  actions,
  onAction,
  isProcessing,
}) => {
  const { t } = useTranslation();
  const [selectedAction, setSelectedAction] = useState(null);
  const [durationHours, setDurationHours] = useState(24);
  const [showConfirm, setShowConfirm] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [reasonText, setReasonText] = useState('');
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleSelectAction = (action) => {
    setSelectedAction(action);
    setShowConfirm(true);
  };

  const toggleReason = (value) => {
    setReasons(prev =>
      prev.includes(value) ? prev.filter(r => r !== value) : [...prev, value]
    );
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const combined = [...images, ...files].slice(0, 5);
    setImages(combined);
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(prev => [...prev, ...newPreviews].slice(0, 5));
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleConfirm = () => {
    if (!selectedAction) return;
    const payload = {
      ...(selectedAction.requiresDuration ? { durationHours } : {}),
      reasons,
      reason: reasonText,
      images,
    };
    onAction(selectedAction.key, payload);
  };

  const handleCancel = () => {
    resetState();
    onClose();
  };

  const handleBack = () => {
    setShowConfirm(false);
    setSelectedAction(null);
  };

  const resetState = () => {
    setSelectedAction(null);
    setShowConfirm(false);
    setDurationHours(24);
    setReasons([]);
    setReasonText('');
    imagePreviews.forEach(url => URL.revokeObjectURL(url));
    setImagePreviews([]);
    setImages([]);
  };

  const showReasons = selectedAction?.requiresReasons !== false;

  return (
    <div className="moderation-action-overlay" onClick={handleCancel}>
      <div className="moderation-action-modal" onClick={(e) => e.stopPropagation()}>
        {!showConfirm ? (
          <>
            <div className="moderation-action-modal-header">
              <h3>{title}</h3>
              <button className="moderation-action-close" onClick={handleCancel}>✕</button>
            </div>
            <div className="moderation-action-list">
              {actions.map((action) => (
                <button
                  key={action.key}
                  className={`moderation-action-item ${action.variant || ''}`}
                  onClick={() => handleSelectAction(action)}
                  disabled={isProcessing}
                >
                  <div className="moderation-action-item-icon">{action.icon}</div>
                  <div className="moderation-action-item-content">
                    <span className="moderation-action-item-label">{action.label}</span>
                    {action.description && (
                      <span className="moderation-action-item-desc">{action.description}</span>
                    )}
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="moderation-action-modal-header">
              <button className="moderation-action-back" onClick={handleBack}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
              <h3>{selectedAction?.confirmTitle || selectedAction?.label}</h3>
              <button className="moderation-action-close" onClick={handleCancel}>✕</button>
            </div>
            <div className="moderation-action-confirm-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {selectedAction?.confirmMessage && (
                <p className="moderation-action-confirm-message">{selectedAction.confirmMessage}</p>
              )}

              {selectedAction?.requiresDuration && (
                <div className="moderation-action-duration">
                  <label>{t('moderation.durationLabel', 'Duration')}</label>
                  <select
                    value={durationHours}
                    onChange={(e) => setDurationHours(Number(e.target.value))}
                    disabled={isProcessing}
                  >
                    {DURATION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {showReasons && (
                <div className="moderation-action-reasons">
                  <label className="moderation-action-section-label">
                    {t('moderation.reasonsLabel', 'Reason(s) for this action')}
                  </label>
                  <div className="moderation-action-reason-chips">
                    {REASON_OPTIONS.map(({ value, labelKey, defaultLabel }) => {
                      const selected = reasons.includes(value);
                      return (
                        <button
                          key={value}
                          type="button"
                          className={`moderation-action-reason-chip ${selected ? 'selected' : ''}`}
                          onClick={() => toggleReason(value)}
                          disabled={isProcessing}
                        >
                          {selected && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          )}
                          {t(labelKey, defaultLabel)}
                        </button>
                      );
                    })}
                  </div>

                  <div className="moderation-action-reason-other open">
                    <textarea
                      rows={2}
                      value={reasonText}
                      onChange={(e) => setReasonText(e.target.value)}
                      placeholder={t('moderation.otherReasonPlaceholder', 'Please specify the reason...')}
                      disabled={isProcessing}
                    />
                  </div>

                  {reasons.length > 0 && (
                    <div className="moderation-action-reason-count">
                      {reasons.length} {t('moderation.reasonsSelected', 'reason(s) selected')}
                    </div>
                  )}
                </div>
              )}

              {/* Image Upload */}
              <div className="moderation-action-images">
                <label className="moderation-action-section-label">
                  {t('moderation.evidenceImages', 'Evidence Images')}
                  <span className="moderation-action-section-optional">{t('common.optional', 'Optional')} • {images.length}/5</span>
                </label>
                <div className="moderation-action-image-grid">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="moderation-action-image-thumb">
                      <img src={preview} alt="" />
                      <button
                        type="button"
                        className="moderation-action-image-remove"
                        onClick={() => removeImage(index)}
                        disabled={isProcessing}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  {images.length < 5 && (
                    <button
                      type="button"
                      className="moderation-action-image-add"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isProcessing}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </button>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                />
              </div>
            </div>
            <div className="moderation-action-confirm-footer">
              <button className="moderation-action-btn-cancel" onClick={handleBack} disabled={isProcessing}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className={`moderation-action-btn-confirm ${selectedAction?.variant || ''}`}
                onClick={handleConfirm}
                disabled={isProcessing}
              >
                {isProcessing ? t('common.processing', 'Processing...') : (selectedAction?.confirmButton || t('common.confirm', 'Confirm'))}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ModerationActionModal;
